import { AfterViewInit, Component, ElementRef, EventEmitter, HostBinding, Input, OnDestroy, Output, ViewChild } from '@angular/core';

import { fromEvent, Observable, Subscription } from 'rxjs';
import { skipUntil, takeUntil } from 'rxjs/operators';

import { TimelineCanvas, TimelineStep } from '../timeline-model';

import { CommonModule } from '@angular/common';
import { TranslateModule } from '@itonics/ngx-translate-simple';
import * as d3 from 'd3';
import { TimelineUnitConfig, timeUnitConfig } from '../helper/time-unit-config';

@Component({
  selector: '[chart-timeline-range-selector]',
  standalone: true,
  imports: [CommonModule, TranslateModule],
  template: `
    <!-- INTERACTIVE RECT -->
    <svg:rect
      #rangeSelectorRect
      class="interactive-rect"
      [attr.x]="actualCanvas.margin.left"
      [attr.y]="actualCanvas.margin.top"
      [attr.height]="actualCanvas.height - actualCanvas.margin.bottom - actualCanvas.margin.top"
      [attr.width]="actualCanvas.width - actualCanvas.margin.left - actualCanvas.margin.left"
    />

    <svg:g [attr.transform]="rectTransform">
      <svg:g class="range-selector" *ngIf="rectWidth > 0">
        <!-- RANGE RECT -->

        <svg:rect
          class="range-selector-rect"
          x="0"
          [attr.y]="actualCanvas.margin.top"
          [attr.height]="actualCanvas.height - actualCanvas.margin.bottom - actualCanvas.margin.top"
          [attr.width]="rectWidth"
        />

        <!-- BORDERS -->
        <svg:line
          class="border-range-selector-rect"
          [attr.x1]="0"
          [attr.y1]="actualCanvas.margin.top"
          [attr.x2]="0"
          [attr.y2]="actualCanvas.height - actualCanvas.margin.bottom"
        />
        <svg:line
          *ngIf="rectWidth > 1"
          class="border-range-selector-rect"
          [attr.x1]="rectWidth"
          [attr.y1]="actualCanvas.margin.top"
          [attr.x2]="rectWidth"
          [attr.y2]="actualCanvas.height - actualCanvas.margin.bottom"
        />

        <!-- CIRCLES -->
        <svg:circle class="circle-range-selector-rect" [attr.cx]="0" [attr.cy]="actualCanvas.margin.top" r="3.5" />
        <svg:circle
          *ngIf="rectWidth > 1"
          class="circle-range-selector-rect"
          [attr.cx]="rectWidth"
          [attr.cy]="actualCanvas.margin.top"
          r="3.5"
        />

        <!-- LABELS -->

        <!-- label only to text with a white border -->
        <svg:g *ngFor="let label of datesLabel; let i = index">
          <svg:text
            class="label-range-selector-rect-back"
            dy="-10"
            text-anchor="middle"
            [attr.x]="i * rectWidth"
            [attr.y]="actualCanvas.margin.top"
          >
            {{ label }}
          </svg:text>

          <svg:text
            class="label-range-selector-rect"
            dy="-10"
            text-anchor="middle"
            [attr.x]="i * rectWidth"
            [attr.y]="actualCanvas.margin.top"
          >
            {{ label }}
          </svg:text>
        </svg:g>

        <!-- CLOSE BUTTON RANGE SELECTOR -->

        <svg:g
          *ngIf="rectWidth"
          class="clear-btn"
          (click)="closeRangeSelector($event)"
          [attr.transform]="' translate(' + rectWidth / 2 + ',' + actualCanvas.margin.top + ')'"
        >
          <svg:rect
            class="clear-btn-back"
            [attr.x]="-rectWidth / 2"
            y="-8"
            [attr.width]="rectWidth"
            height="16"
            fill="white"
            fill-opacity="0"
          />
          <svg:rect class="clear-btn-rect" x="-26" y="-8" width="52" height="16" rx="8" />
          <svg:text class="clear-btn-text" dx="0" dy="4" text-anchor="middle" [attr.x]="0" [attr.y]="0">
            {{ 'chart-ng.timeline.selection.clear' | translate }}
          </svg:text>
        </svg:g>
      </svg:g>
    </svg:g>
  `,
  styleUrls: ['./timeline-range-selector.component.scss']
})
export class TimelineRangeSelectorComponent implements OnDestroy, AfterViewInit {
  public actualCanvas: TimelineCanvas & { clientX?: number };

  @Input()
  dateRange: Array<Date>;

  // Step is the unit of time
  // used to format the timeline
  @Input()
  unitTime: TimelineStep;

  @ViewChild('rangeSelectorRect')
  rangeSelectorRect: ElementRef;

  @Output()
  rangeSelectModelChange: EventEmitter<Array<Date>> = new EventEmitter();

  private rangeIOModel: Array<Date>;

  @Input()
  get rangeSelectModel() {
    return this.rangeIOModel;
  }

  set rangeSelectModel(val) {
    if (this.rangeIOModel === val) {
      return;
    }

    this.rangeIOModel = val; // io management

    if (!this.rangeIOModel) {
      this.closeRangeSelector();
      return;
    }
    this.drawSelection();
  }

  @Input()
  set canvas(canvas: TimelineCanvas) {
    this.actualCanvas = canvas;
    if (canvas && this.rangeIOModel) {
      this.drawSelection();
    }
    // Set canvas' clientX value for calculating click position
    if (this.rangeSelectorRect) {
      // Will only fire if canvas size changes after component is initialized
      this.actualCanvas.clientX = this.rangeSelectorRect.nativeElement.getBoundingClientRect().x - canvas.margin.left;
    }
  }

  /**
   * MOUSE EVENTS
   */
  private mousedownCanvas: Observable<MouseEvent>;
  private mouseupCanvas: Observable<MouseEvent>;
  private mousemoveCanvas: Observable<MouseEvent>;

  private mouseupWindow: Observable<Event>;
  private keyupWindow: Observable<Event>;

  private mouseMoveSub: Subscription;

  /**
   * Range selector RECT
   */

  public rectTransform: string;
  private rectPosition: number;
  public rectWidth: number = 0;

  /**
   * LABELS
   */

  public datesLabel: Array<any>;

  /*
    SUBSCRIPTIONS
  */

  private subs: Subscription = new Subscription();

  /**
   * STATES
   */

  @HostBinding('class.on-selection')
  onSelection: boolean;

  ngAfterViewInit() {
    const elm = this.rangeSelectorRect.nativeElement;
    // Set canvas' clientX value for calculating click position
    if (this.actualCanvas) {
      this.actualCanvas.clientX = this.rangeSelectorRect.nativeElement.getBoundingClientRect().x - this.actualCanvas.margin.left;
    }

    /**
     * We create Observables for different types of events that we want to track
     */
    //  Mouse Ones
    this.mousedownCanvas = fromEvent(elm, 'mousedown');
    this.mouseupCanvas = fromEvent(elm, 'mouseup');
    this.mousemoveCanvas = fromEvent(elm, 'mousemove');

    //  Window Ones
    this.mouseupWindow = fromEvent(window, 'mouseup');
    this.keyupWindow = fromEvent(window, 'keyup');

    /**
     * We link the event mouseMove with mouse down and terminates in mouseUp
     */

    this.mousemoveCanvas = this.mousemoveCanvas.pipe(skipUntil(this.mousedownCanvas)).pipe(takeUntil(this.mouseupCanvas));

    /**
     * This event is the beginning of the selection here we
     * record the position where to start and we reset the width to zero
     */

    this.subs.add(
      this.mousedownCanvas.subscribe((event: MouseEvent) => {
        // Width & X position
        this.rectWidth = 0;
        this.rectPosition = event.clientX - this.actualCanvas.clientX; // Click position relative range selector's parent

        this.rectTransform = `translate(${this.rectPosition}, 0)`;

        // Labels
        this.getRangesLabelsOnMouseInteraction(this.rectPosition);
      })
    );

    /**
     * With this event we get the mouseUp event ONLY in canvas
     * and terminates the selection
     */

    this.subs.add(
      this.mouseupCanvas.subscribe((event: MouseEvent) => {
        event.stopPropagation();

        this.onSelection = false;

        //  We want to draw a line if the user only clicks in the
        // canvas so we check if the rect if zero we can draw a line
        this.rectWidth = this.rectWidth || 1;

        // In this step we overwrite the model ONLY if there is something selected
        this.rangeSelectModel = this.rangeIOModel;
        //  We only emit when on mouse up that is when the interaction finishes and there is something selected
        this.rangeSelectModelChange.emit(this.rangeIOModel);

        // We register again the subscription to mouse down+move
        this.registerDownAndMoveEvent();
      })
    );

    /**
     * In the case where the user moves the mouse outside of the canvas
     * meanwhile is selecting a range
     * we want to finish the selection otherwise the range selector misbehave
     */

    this.subs.add(
      this.mouseupWindow.subscribe((event: MouseEvent) => {
        //  We check if the mouseMove subscription listening, this means that the selection is still open
        if (this.onSelection) {
          // In this step we overwrite the model ONLY if there is something selected
          this.rangeSelectModel = this.rangeIOModel;
          //  We only emit when on mouse up that is when the interaction finishes and there is something selected
          this.rangeSelectModelChange.emit(this.rangeIOModel);
          // We register again the subscription to mouse down+move
          this.registerDownAndMoveEvent();
        }
      })
    );

    /**
     *  Escape to close the range selector
     */

    this.subs.add(
      this.keyupWindow.subscribe((event: KeyboardEvent) => {
        if (event.code === 'Escape' || event.keyCode === 27 || event.which === 27) {
          if (this.rangeSelectModel) {
            //  Here we want to clean the selection and notify
            this.rectWidth = 0;
            this.rangeSelectModel = null;
            this.rangeSelectModelChange.emit(this.rangeIOModel);
          }
        }
      })
    );

    /**
     *  We register for the first time the combine event of down and move
     */
    this.registerDownAndMoveEvent();
  }

  ngOnDestroy() {
    this.subs.unsubscribe();
  }

  private registerDownAndMoveEvent() {
    if (this.mouseMoveSub) {
      this.mouseMoveSub.unsubscribe();
    }

    this.mouseMoveSub = this.mousemoveCanvas.subscribe((event: MouseEvent) => {
      this.drawRangeOnMouseInteraction(event);
      this.onSelection = true;
    });
  }

  private drawRangeOnMouseInteraction(event: MouseEvent) {
    const clickX = event.clientX - this.actualCanvas.clientX; // Click position relative range selector's parent
    if (clickX - this.rectPosition < 0) {
      this.rectTransform = `translate(${clickX}, 0)`;
      this.rectWidth = this.rectPosition - clickX;
      this.getRangesLabelsOnMouseInteraction(clickX);
    } else {
      this.rectWidth = clickX - this.rectPosition;
      this.getRangesLabelsOnMouseInteraction(this.rectPosition);
    }
  }

  private getRangesLabelsOnMouseInteraction(position: number) {
    if (this.dateRange) {
      const unitConfig: TimelineUnitConfig = timeUnitConfig(this.unitTime);
      //  Convert the position of the cursor in the canvas to Time
      const dateA = this.canvasToTimeScale()(position - this.actualCanvas.margin.left);

      // Set Model
      this.rangeIOModel = [dateA];
      // Set Label
      this.datesLabel = [unitConfig.formatSelection(dateA)];

      if (this.rectWidth > 1) {
        //  Convert the width of the selection in the canvas to Time
        const dateB = this.canvasToTimeScale()(position - this.actualCanvas.margin.left + this.rectWidth);

        // Set Model
        this.rangeIOModel.push(dateB);
        // Set Label
        this.datesLabel.push(unitConfig.formatSelection(dateB));
      }
    }
  }

  private canvasToTimeScale() {
    return d3
      .scaleLinear()
      .domain([0, this.actualCanvas.width - this.actualCanvas.margin.left - this.actualCanvas.margin.left])
      .range(this.dateRange);
  }

  private timeToCanvasScale() {
    return d3
      .scaleLinear()
      .domain(this.dateRange)
      .range([0, this.actualCanvas.width - this.actualCanvas.margin.left - this.actualCanvas.margin.left]);
  }

  public closeRangeSelector(event?: MouseEvent) {
    if (this.mouseMoveSub) {
      this.mouseMoveSub.unsubscribe();
      this.rectWidth = 0;
      this.rangeSelectModel = null;
      setTimeout(() => {
        this.registerDownAndMoveEvent();
      }, 0);
    }
    if (event) {
      event.stopPropagation();
    }
    setTimeout(() => {
      this.rangeSelectModelChange.emit(this.rangeIOModel);
    }, 0);
  }

  private drawSelection() {
    const scale = this.timeToCanvasScale();
    const x1 = scale(new Date(this.rangeIOModel[0]));
    const x2 = scale(new Date(this.rangeIOModel[1]));

    const unitConfig: TimelineUnitConfig = timeUnitConfig(this.unitTime);

    this.rectTransform = `translate(${this.actualCanvas.margin.left + x1}, 0)`;
    this.rectWidth = x2 - x1;

    this.datesLabel = [
      unitConfig.formatSelection(new Date(this.rangeIOModel[0])),
      unitConfig.formatSelection(new Date(this.rangeIOModel[1]))
    ];
  }
}
